import React, { useState } from 'react';
import { CheckCircle, RefreshCw } from 'lucide-react';

/** Placeholder when thumbnail fails to load (React state, no innerHTML). */
function ThumbnailWithError({ src, alt, variant = 'accepted' }) {
  const [failed, setFailed] = useState(false);
  const isAccepted = variant === 'accepted';
  if (failed) {
    return (
      <div
        className={`w-20 h-20 rounded-lg border-2 flex items-center justify-center text-xs ${
          isAccepted ? 'border-green-300 bg-green-50 text-green-700' : 'border-red-200 bg-red-50 text-red-700'
        }`}
        role="img"
        aria-label="Błąd ładowania miniatury"
      >
        Błąd
      </div>
    );
  }
  return (
    <div className="relative">
      <img
        src={src || '#'}
        alt={alt}
        className={`w-20 h-20 object-cover rounded-lg border-2 ${
          isAccepted ? 'border-green-500' : 'border-red-500 opacity-60'
        }`}
        onError={() => setFailed(true)}
      />
      <div
        className={`absolute top-0 right-0 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center ${
          isAccepted ? 'bg-green-500' : 'bg-red-500'
        }`}
        aria-hidden
      >
        {isAccepted ? '✓' : '✗'}
      </div>
    </div>
  );
}

function ValidationResults({ results, onReset, onAddOpinion, onFixRejected }) {
  const { results: validationResults, summary, reviewId, orderInfo, acceptedImages, rejectedImages } = results;

  const canAddOpinion =
    summary?.canAcceptReview === true &&
    summary?.accepted >= 3 &&
    orderInfo?.textReview &&
    orderInfo.textReview.trim().length >= 20;
  const opinionAlreadyAdded = reviewId != null;
  const hasRejectedImages = (summary?.rejected ?? 0) > 0;
  const rejectedResults = validationResults?.filter((r) => r.decision === 'reject') || [];

  return (
    <div className="space-y-6">
      {canAddOpinion && !opinionAlreadyAdded && (
        <section className="text-center" aria-labelledby="success-heading">
          <p id="success-heading" className="text-lg font-semibold text-green-700 mb-4">
            {summary?.reviewMessage || 'Gratulacje, Twoje zdjęcia zostały wstępnie zaakceptowane!'}
          </p>

          {acceptedImages?.length > 0 && (
            <div className="mb-6">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
                Zaakceptowane zdjęcia ({acceptedImages.length})
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {acceptedImages.map((img, index) => (
                  <ThumbnailWithError
                    key={index}
                    src={img.url}
                    alt={img.filename || `Zdjęcie ${index + 1}`}
                    variant="accepted"
                  />
                ))}
              </div>
            </div>
          )}

          {summary?.rejectedWithPeople > 0 && summary?.canAcceptReview && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-left">
              <p className="text-sm text-yellow-800 mb-2">
                Odrzucono {summary.rejectedWithPeople}{' '}
                {summary.rejectedWithPeople === 1 ? 'zdjęcie' : 'zdjęć'} z osobami. Masz wystarczająco dużo dobrych
                zdjęć.
              </p>
              {rejectedImages?.filter((img) => img.people).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {rejectedImages.filter((img) => img.people).map((img, index) => (
                    <ThumbnailWithError
                      key={index}
                      src={img.url}
                      alt={img.filename || `Odrzucone ${index + 1}`}
                      variant="rejected"
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={onAddOpinion}
            className="px-6 py-3 rounded-lg font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 min-h-[44px]"
          >
            Dodaj swoją opinię do systemu
          </button>
        </section>
      )}

      {!canAddOpinion && !opinionAlreadyAdded && summary && (
        <section className="text-center p-6 bg-yellow-50 border border-yellow-200 rounded-lg" role="alert">
          <p className="text-lg font-semibold text-yellow-800 mb-4">
            {summary.reviewMessage ||
              `Potrzebujesz jeszcze ${(summary.minimumRequired ?? 3) - (summary.accepted ?? 0)} dobrych zdjęć`}
          </p>
          {summary.needsMoreImages?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">Zdjęcia do poprawy</p>
              <ul className="text-sm text-gray-700 space-y-1">
                {summary.needsMoreImages.map((filename, index) => (
                  <li key={index} className="flex items-center justify-center gap-2">
                    <span className="text-red-500" aria-hidden>✗</span>
                    <span>{filename}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-600 mt-4">
                Wymień te zdjęcia na lepsze i kliknij „Popraw błędne zdjęcia” poniżej.
              </p>
            </div>
          )}
        </section>
      )}

      {reviewId && (
        <section
          className="rounded-xl border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-6 shadow-sm"
          aria-labelledby="thanks-heading"
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" aria-hidden>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 id="thanks-heading" className="text-2xl font-bold text-green-900 mb-2">
              Dziękujemy za dodanie opinii!
            </h2>
            <p className="text-green-700 mb-4">Twoja opinia została zapisana i czeka na weryfikację.</p>
            <div className="bg-white rounded-lg p-6 border border-green-200 text-left max-w-md mx-auto">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">Kod rabatowy</p>
              <p className="text-gray-900 mb-2">
                Zostanie wysłany na Twój adres email po akceptacji opinii przez administratora.
              </p>
              <p className="text-sm text-gray-600">Wartość: 100 zł zniżki na następne zamówienie.</p>
            </div>
          </div>
        </section>
      )}

      {hasRejectedImages && !opinionAlreadyAdded && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => onFixRejected(rejectedResults)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 min-h-[44px]"
          >
            <RefreshCw className="w-4 h-4" aria-hidden />
            <span>Popraw błędne zdjęcia ({summary?.rejected ?? 0})</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default ValidationResults;

